// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - Shared form for creating and updating
//           Session Reference Content

import React, {
    useMemo,
    useState,
} from "react"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = value => {

    if (!value) return ""

    if (typeof value === "string") return value

    return value?._id || value?.id || ""

}


const toList = data => {

    return (
        data?.ids || []
    ).map(
        id => data.entities[id]
    ).filter(Boolean)

}


const extractYouTubeVideoId = input => {

    const value = String(input || "").trim()

    if (
        /^[a-zA-Z0-9_-]{11}$/.test(value)
    ) {
        return value
    }

    try {

        const url = new URL(value)

        const hostname =
            url.hostname.toLowerCase()

        let videoId = ""

        if (
            hostname === "youtu.be" ||
            hostname === "www.youtu.be"
        ) {

            videoId = url.pathname
                .split("/")
                .filter(Boolean)[0] || ""

        } else if (
            [
                "youtube.com",
                "www.youtube.com",
                "m.youtube.com",
                "youtube-nocookie.com",
                "www.youtube-nocookie.com",
            ].includes(hostname)
        ) {

            videoId =
                url.searchParams.get("v") || ""

            if (!videoId) {

                const segments = url.pathname
                    .split("/")
                    .filter(Boolean)

                if (
                    ["embed", "shorts", "live"].includes(
                        segments[0]
                    )
                ) {
                    videoId = segments[1] || ""
                }

            }

        }

        return /^[a-zA-Z0-9_-]{11}$/.test(videoId)
            ? videoId
            : ""

    } catch {

        return ""

    }

}


const isValidWebUrl = value => {

    try {

        const url = new URL(value)

        return (
            url.protocol === "https:" ||
            url.protocol === "http:"
        )

    } catch {

        return false

    }

}


let nextPromptId = 1

const newPrompt = order => ({

    localId: `new-${nextPromptId++}`,

    promptText: "",

    displayOrder: order,

    active: true,

})


/*
|--------------------------------------------------------------------------
| 02 - Initial Form State
|--------------------------------------------------------------------------
*/

const initialFormState = {

    trailId: "",

    sessionId: "",

    contentType: "Video",

    title: "",

    description: "",

    contentProvider: "YouTube",

    videoId: "",

    contentUrl: "",

    displayOrder: 1,

    active: true,

}


/*
|--------------------------------------------------------------------------
| 03 - Main Component
|--------------------------------------------------------------------------
*/

const EtSessionReferenceContentForm = ({
    initialContent = null,
    onSave,
    onCancel,
    isSaving = false,
    formTitle = "Session Reference Content",
    submitLabel = "Create",
}) => {

    const isUpdate =
        !!initialContent


    /*
    |--------------------------------------------------------------------------
    | 04 - Master Queries
    |--------------------------------------------------------------------------
    */

    const {
        data: trailsData,
        isLoading: isTrailsLoading,
    } = useGetTrailsQuery(
        "trailsList"
    )

    const {
        data: sessionsData,
        isLoading: isSessionsLoading,
    } = useGetSessionsQuery(
        "sessionsList"
    )


    const trailList = useMemo(
        () => toList(trailsData),
        [trailsData]
    )

    const sessionList = useMemo(
        () => toList(sessionsData),
        [sessionsData]
    )


    /*
    |--------------------------------------------------------------------------
    | 05 - Resolve Initial Session And Trail
    |--------------------------------------------------------------------------
    */

    const initialSessionId =
        getRefId(
            initialContent?.sessionId
        )

    const initialSession = sessionList.find(
        session =>
            getRefId(session) === initialSessionId
    )

    const initialTrailId =
        getRefId(initialSession?.trailId) ||
        getRefId(initialContent?.sessionId?.trailId)


    /*
    |--------------------------------------------------------------------------
    | 06 - Form State
    |--------------------------------------------------------------------------
    */

    const [
        formData,
        setFormData,
    ] = useState(() => ({

        ...initialFormState,

        ...(initialContent
            ? {

                sessionId:
                    getRefId(initialContent.sessionId),

                contentType:
                    initialContent.contentType || "Video",

                title:
                    initialContent.title || "",

                description:
                    initialContent.description || "",

                contentProvider:
                    initialContent.contentProvider || "YouTube",

                videoId:
                    initialContent.videoId || "",

                contentUrl:
                    initialContent.contentUrl || "",

                displayOrder:
                    initialContent.displayOrder ?? 1,

                active:
                    initialContent.active !== false,

            }
            : {}),

    }))


    const [
        reflectionPrompts,
        setReflectionPrompts,
    ] = useState(
        () => (
            initialContent?.reflectionPrompts || []
        ).map(
            (prompt, index) => ({

                localId:
                    prompt._id ||
                    `initial-${index}`,

                promptText:
                    prompt.promptText || "",

                displayOrder:
                    prompt.displayOrder ?? index + 1,

                active:
                    prompt.active !== false,

            })
        )
    )


    const [
        errorMessage,
        setErrorMessage,
    ] = useState("")


    /*
    |--------------------------------------------------------------------------
    | 07 - Trail Selection
    |--------------------------------------------------------------------------
    |
    | Trail is a UI selection context.
    | Only sessionId is stored in SessionReferenceContent.
    |
    |--------------------------------------------------------------------------
    */

    const [
        selectedTrailId,
        setSelectedTrailId,
    ] = useState("")


    const effectiveTrailId =
        selectedTrailId ||
        (
            formData.sessionId === initialSessionId
                ? initialTrailId
                : ""
        )


    /*
    |--------------------------------------------------------------------------
    | 08 - Filtered Sessions
    |--------------------------------------------------------------------------
    */

    const filteredSessions = useMemo(
        () => {

            if (!effectiveTrailId) {
                return []
            }

            return sessionList
                .filter(
                    session =>
                        getRefId(session.trailId) ===
                            effectiveTrailId &&
                        (
                            session.active !== false ||
                            (
                                isUpdate &&
                                getRefId(session) ===
                                    initialSessionId
                            )
                        )
                )
                .sort(
                    (a, b) =>
                        (
                            a.sessionSequence ?? 999999
                        ) -
                        (
                            b.sessionSequence ?? 999999
                        )
                )

        },
        [
            sessionList,
            effectiveTrailId,
            isUpdate,
            initialSessionId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 09 - Change Handlers
    |--------------------------------------------------------------------------
    */

    const handleChange = e => {

        const {
            name,
            value,
            type,
            checked,
        } = e.target

        setFormData(
            previous => ({

                ...previous,

                [name]:
                    type === "checkbox"
                        ? checked
                        : value,

            })
        )

    }


    const handleTrailChange = e => {

        setSelectedTrailId(
            e.target.value
        )

        setFormData(
            previous => ({

                ...previous,

                sessionId: "",

            })
        )

    }


    const handleContentTypeChange = e => {

        const contentType =
            e.target.value

        setFormData(
            previous => ({

                ...previous,

                contentType,

                contentProvider:
                    contentType === "Video"
                        ? "YouTube"
                        : "External",

            })
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 10 - Reflection Prompt Management
    |--------------------------------------------------------------------------
    */

    const handleAddPrompt = () => {

        setReflectionPrompts(
            previous => [

                ...previous,

                newPrompt(
                    previous.length + 1
                ),

            ]
        )

    }


    const handlePromptChange = (
        localId,
        field,
        value
    ) => {

        setReflectionPrompts(
            previous =>
                previous.map(
                    prompt =>
                        prompt.localId === localId
                            ? {
                                ...prompt,
                                [field]: value,
                            }
                            : prompt
                )
        )

    }


    const handleRemovePrompt = localId => {

        setReflectionPrompts(
            previous =>
                previous.filter(
                    prompt =>
                        prompt.localId !== localId
                )
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 11 - Validate Form
    |--------------------------------------------------------------------------
    */

    const validateForm = () => {

        if (!formData.sessionId) {

            return "Please select a Session."

        }

        if (!formData.title.trim()) {

            return "Content title is required."

        }

        if (
            formData.contentType === "Video" &&
            formData.contentProvider === "YouTube"
        ) {

            if (
                !extractYouTubeVideoId(
                    formData.videoId
                )
            ) {

                return (
                    "Enter a valid YouTube Video ID or URL."
                )

            }

        } else if (
            !isValidWebUrl(
                formData.contentUrl
            )
        ) {

            return (
                "Enter a valid HTTP or HTTPS content URL."
            )

        }

        return ""

    }


    /*
    |--------------------------------------------------------------------------
    | 12 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async e => {

        e.preventDefault()

        setErrorMessage("")

        const validationError =
            validateForm()

        if (validationError) {

            setErrorMessage(
                validationError
            )

            return

        }


        /*
        |--------------------------------------------------------------------------
        | Prepare Reflection Prompts
        |--------------------------------------------------------------------------
        */

        const preparedPrompts =
            reflectionPrompts
                .filter(
                    prompt =>
                        prompt.promptText.trim()
                )
                .map(
                    (prompt, index) => ({

                        promptText:
                            prompt.promptText.trim(),

                        displayOrder:
                            Number(
                                prompt.displayOrder
                            ) || index + 1,

                        active:
                            prompt.active !== false,

                    })
                )


        /*
        |--------------------------------------------------------------------------
        | Prepare Content Payload
        |--------------------------------------------------------------------------
        */

        const isYouTube =
            formData.contentType === "Video" &&
            formData.contentProvider === "YouTube"

        const payload = {

            sessionId:
                formData.sessionId,

            contentType:
                formData.contentType,

            title:
                formData.title.trim(),

            description:
                formData.description.trim(),

            contentProvider:
                formData.contentProvider,

            videoId:
                isYouTube
                    ? extractYouTubeVideoId(
                        formData.videoId
                    )
                    : "",

            contentUrl:
                isYouTube
                    ? ""
                    : formData.contentUrl.trim(),

            reflectionPrompts:
                preparedPrompts,

            displayOrder:
                Number(
                    formData.displayOrder
                ) || 0,

            active:
                formData.active,

        }


        /*
        |--------------------------------------------------------------------------
        | Save
        |--------------------------------------------------------------------------
        */

        try {

            await onSave(payload)

        } catch (error) {

            console.error(error)

            setErrorMessage(
                error?.data?.message ||
                error?.error ||
                "Unable to save reference content."
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Clear
    |--------------------------------------------------------------------------
    */

    const handleClear = () => {

        if (isUpdate) {

            setFormData({

                ...initialFormState,

                sessionId:
                    getRefId(
                        initialContent.sessionId
                    ),

                contentType:
                    initialContent.contentType,

                title:
                    initialContent.title || "",

                description:
                    initialContent.description || "",

                contentProvider:
                    initialContent.contentProvider || "YouTube",

                videoId:
                    initialContent.videoId || "",

                contentUrl:
                    initialContent.contentUrl || "",

                displayOrder:
                    initialContent.displayOrder ?? 1,

                active:
                    initialContent.active !== false,

            })

            setSelectedTrailId("")

            setReflectionPrompts(
                (
                    initialContent.reflectionPrompts || []
                ).map(
                    (prompt, index) => ({

                        localId:
                            prompt._id ||
                            `initial-${index}`,

                        promptText:
                            prompt.promptText || "",

                        displayOrder:
                            prompt.displayOrder ?? index + 1,

                        active:
                            prompt.active !== false,

                    })
                )
            )

        } else {

            setFormData(
                initialFormState
            )

            setSelectedTrailId("")

            setReflectionPrompts([])

        }

        setErrorMessage("")

    }


    /*
    |--------------------------------------------------------------------------
    | 14 - Content Location
    |--------------------------------------------------------------------------
    */

    const isYouTube =
        formData.contentType === "Video" &&
        formData.contentProvider === "YouTube"


    /*
    |--------------------------------------------------------------------------
    | 15 - Loading
    |--------------------------------------------------------------------------
    */

    if (
        isTrailsLoading ||
        isSessionsLoading
    ) {

        return (
            <PulseLoader color="#ea580c" />
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 16 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black font-poppins">

            <div className="bg-white p-1 m-1 rounded">

                <p className="p-1 m-1 font-bold text-xl">
                    {formTitle}
                </p>


                <div className="bg-gray-200 rounded m-1 p-1">

                    <form
                        onSubmit={handleSubmit}
                        className="m-1 p-1 text-xs"
                    >


                        {/* Trail */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Trail
                            </p>

                            <select
                                value={effectiveTrailId}
                                onChange={handleTrailChange}
                                disabled={isSaving}
                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >

                                <option value="">
                                    Select Trail
                                </option>

                                {trailList
                                    .filter(
                                        trail =>
                                            trail.active !== false
                                    )
                                    .map(
                                        trail => (

                                            <option
                                                key={getRefId(trail)}
                                                value={getRefId(trail)}
                                            >
                                                {trail.trailName}
                                            </option>

                                        )
                                    )}

                            </select>

                        </div>


                        {/* Session */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Session
                            </p>

                            <select
                                name="sessionId"
                                value={formData.sessionId}
                                onChange={handleChange}
                                disabled={
                                    !effectiveTrailId ||
                                    isSaving
                                }
                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >

                                <option value="">
                                    Select Session
                                </option>

                                {filteredSessions.map(
                                    session => (

                                        <option
                                            key={getRefId(session)}
                                            value={getRefId(session)}
                                        >
                                            {session.sessionSequence
                                                ? `${session.sessionSequence} - `
                                                : ""}
                                            {session.sessionName}
                                        </option>

                                    )
                                )}

                            </select>

                        </div>


                        {/* Content Type */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Content Type
                            </p>

                            <select
                                name="contentType"
                                value={formData.contentType}
                                onChange={handleContentTypeChange}
                                disabled={isSaving}
                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >

                                <option value="Video">
                                    Video
                                </option>

                                <option value="Article">
                                    Article
                                </option>

                                <option value="Blog">
                                    Blog
                                </option>

                                <option value="Other">
                                    Other
                                </option>

                            </select>

                        </div>


                        {/* Title */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Content Title
                            </p>

                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter reference content title"
                                disabled={isSaving}
                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                        </div>


                        {/* Description */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Description
                            </p>

                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                placeholder="Describe the resource and its relevance to the Session"
                                disabled={isSaving}
                                className="w-full rounded p-2 m-1 bg-gray-100 resize-y"
                            />

                        </div>


                        {/* Provider */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Content Provider
                            </p>

                            <select
                                name="contentProvider"
                                value={formData.contentProvider}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >

                                {formData.contentType === "Video" && (

                                    <option value="YouTube">
                                        YouTube
                                    </option>

                                )}

                                <option value="External">
                                    External
                                </option>

                            </select>

                        </div>


                        {/* Video ID Or URL */}

                        <div className="bg-white rounded m-1 p-2">

                            {isYouTube ? (

                                <>

                                    <p className="font-bold text-xs m-1">
                                        YouTube Video ID or URL
                                    </p>

                                    <input
                                        type="text"
                                        name="videoId"
                                        value={formData.videoId}
                                        onChange={handleChange}
                                        disabled={isSaving}
                                        placeholder="YouTube Video ID or full URL"
                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />

                                    <p className="text-[10px] text-gray-500 m-1">
                                        You can paste a regular YouTube
                                        URL, Shorts URL or the video ID.
                                    </p>

                                </>

                            ) : (

                                <>

                                    <p className="font-bold text-xs m-1">
                                        Content URL
                                    </p>

                                    <input
                                        type="url"
                                        name="contentUrl"
                                        value={formData.contentUrl}
                                        onChange={handleChange}
                                        disabled={isSaving}
                                        placeholder="https://..."
                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />

                                </>

                            )}

                        </div>


                        {/* Display Order */}

                        <div className="bg-white rounded m-1 p-2">

                            <p className="font-bold text-xs m-1">
                                Display Order
                            </p>

                            <input
                                type="number"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="w-32 rounded p-2 m-1 bg-gray-100"
                            />

                        </div>


                        {/* Active */}

                        <div className="bg-white rounded m-1 p-2">

                            <label className="flex items-center bg-gray-100 rounded m-1 p-2">

                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                    className="m-1"
                                />

                                <span className="text-xs m-1">
                                    Active Reference Content
                                </span>

                            </label>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | Reflection Prompts
                        |--------------------------------------------------------------------------
                        */}

                        <div className="bg-white rounded m-1 p-2">

                            <div className="flex items-center justify-between gap-2">

                                <div>

                                    <p className="font-bold text-sm m-1">
                                        Reflection Prompts
                                    </p>

                                    <p className="text-[10px] text-gray-500 m-1">
                                        Optional prompts for deeper introspection.
                                        These are separate from Session Q&A.
                                    </p>

                                </div>

                                <button
                                    type="button"
                                    onClick={handleAddPrompt}
                                    disabled={isSaving}
                                    className="bg-orange-100 text-orange-700 rounded px-3 py-2 text-xs font-bold"
                                >
                                    + Add Prompt
                                </button>

                            </div>


                            {reflectionPrompts.length === 0 && (

                                <div className="bg-gray-100 rounded m-1 p-3">

                                    <p className="text-xs text-gray-500">
                                        No reflection prompts added.
                                    </p>

                                </div>

                            )}


                            {reflectionPrompts.map(
                                (prompt, index) => (

                                    <div
                                        key={prompt.localId}
                                        className="bg-gray-100 rounded m-1 p-3"
                                    >

                                        <div className="flex justify-between items-center">

                                            <p className="text-xs font-bold">
                                                Prompt {index + 1}
                                            </p>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleRemovePrompt(
                                                        prompt.localId
                                                    )
                                                }
                                                disabled={isSaving}
                                                className="text-xs text-red-600"
                                            >
                                                Remove
                                            </button>

                                        </div>


                                        <textarea
                                            rows={3}
                                            value={prompt.promptText}
                                            onChange={e =>
                                                handlePromptChange(
                                                    prompt.localId,
                                                    "promptText",
                                                    e.target.value
                                                )
                                            }
                                            disabled={isSaving}
                                            placeholder="Enter a reflection prompt..."
                                            className="w-full rounded p-2 mt-2 bg-white resize-y text-xs"
                                        />


                                        <div className="flex flex-wrap items-center gap-5 mt-2">

                                            <div className="flex items-center gap-2">

                                                <span className="text-xs">
                                                    Order
                                                </span>

                                                <input
                                                    type="number"
                                                    value={prompt.displayOrder}
                                                    onChange={e =>
                                                        handlePromptChange(
                                                            prompt.localId,
                                                            "displayOrder",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isSaving}
                                                    className="w-20 rounded p-2 bg-white"
                                                />

                                            </div>


                                            <label className="flex items-center gap-2">

                                                <input
                                                    type="checkbox"
                                                    checked={prompt.active}
                                                    onChange={e =>
                                                        handlePromptChange(
                                                            prompt.localId,
                                                            "active",
                                                            e.target.checked
                                                        )
                                                    }
                                                    disabled={isSaving}
                                                />

                                                <span className="text-xs">
                                                    Active
                                                </span>

                                            </label>

                                        </div>

                                    </div>

                                )
                            )}

                        </div>


                        {/* Error */}

                        {errorMessage && (

                            <div className="bg-red-50 text-red-700 rounded m-1 p-3 text-xs">
                                {errorMessage}
                            </div>

                        )}


                        {/* Actions */}

                        <div className="bg-white m-1 px-1 py-3 rounded">

                            {isSaving && (

                                <PulseLoader color="#ea580c" />

                            )}

                            <EtBtnFormBasic
                                name={submitLabel}
                                type="create"
                                handleClick={handleSubmit}
                            />

                            <EtBtnFormBasic
                                name={isUpdate ? "Reset" : "Clear"}
                                type="clear"
                                handleClick={handleClear}
                            />

                            <EtBtnFormBasic
                                name="Cancel"
                                type="cancel"
                                handleClick={onCancel}
                            />

                        </div>

                    </form>

                </div>

            </div>

        </div>

    )

}


export default EtSessionReferenceContentForm